import { differenceInCalendarDays } from "date-fns";
import { IRefund } from "@evennt-tech/api-v2-core";
import type { Timestamp } from "api/firebase";
import {
  ExpandedOrder,
  Invoice,
  OrderStatus,
  PaymentStatusBucket,
} from "ts/api/order";
import { isOrderBankTransfer } from "tools/order/isOrderCardOrIdeal";
import { calculateBankTransferDueDate } from "tools/order/calculateBankTransferDueDate";
import { getOrderRefundStatus } from "tools/order/refund";
import orderStatusesMap, {
  isBankTransferAwaitingPayment,
  isPreAuthorisedOrder,
} from "tools/order/getOrderStatus";
import { MultichoiceDefaultValue } from "view/components/Table/FilterBar/types";

// Single source of truth for the chip values/labels — the same strings are
// read back by Table/helpers.tsx#filterPaymentStatus, so this must be the
// only place `PaymentStatusBucket` values are spelled out for the UI.
export const PAYMENT_STATUS_FILTER: MultichoiceDefaultValue = {
  type: "multichoice",
  label: "Payment status",
  values: [
    { value: "paid", label: "Paid" },
    { value: "notPaid", label: "Not Paid" },
    { value: "preAuthorised", label: "Pre-Authorised" },
    { value: "overdue", label: "Overdue" },
  ],
};

// Stable array reference for the `defaultFilterValues` prop — FilterBar's
// internal effect re-syncs state whenever this array's reference changes
// (see FilterBar/index.tsx's `updateFiltersOnValuesChange` useCallback), so
// a fresh `[PAYMENT_STATUS_FILTER]` literal on every render causes an
// infinite render loop. Consumers must import this constant, not wrap
// PAYMENT_STATUS_FILTER in an inline array themselves.
export const PAYMENT_STATUS_FILTER_VALUES: MultichoiceDefaultValue[] = [
  PAYMENT_STATUS_FILTER,
];

type OrderDueDate = Timestamp | Date;

const toDate = (dueDate: OrderDueDate): Date =>
  dueDate instanceof Date ? dueDate : dueDate.toDate();

export const isInvoiceOverdue = (dueDate?: OrderDueDate): boolean => {
  if (!dueDate) return false;
  return differenceInCalendarDays(new Date(), toDate(dueDate)) > 0;
};

const getRelevantInvoices = (
  order: ExpandedOrder,
  companyId?: string
): Invoice[] => {
  if (!isOrderBankTransfer(order)) return [];
  const invoices = Object.values(order.invoices);
  if (!companyId) return invoices;
  return invoices.filter(
    (invoice) => invoice.products?.[0]?.owner?.id === companyId
  );
};

// Order-level due date used for the Payment column tooltip: the earliest
// due date among the relevant invoices (a bank-transfer order can carry one
// invoice per supplier company).
export const getOrderDueDate = (
  order: ExpandedOrder,
  companyId?: string,
  showLiveStart?: string
): OrderDueDate | undefined => {
  if (isOrderBankTransfer(order)) {
    const canonicalDueDate = calculateBankTransferDueDate(
      order.createdAt.toDate(),
      showLiveStart
    );
    if (canonicalDueDate) return canonicalDueDate;
  }

  const dueDates = getRelevantInvoices(order, companyId)
    .map((invoice) => invoice.dueDate)
    .filter((dueDate): dueDate is Timestamp => !!dueDate);

  if (!dueDates.length) return undefined;
  return dueDates.reduce((earliest, current) =>
    current.toMillis() < earliest.toMillis() ? current : earliest
  );
};

const isOrderOverdue = (
  order: ExpandedOrder,
  companyId?: string,
  showLiveStart?: string
): boolean =>
  isInvoiceOverdue(getOrderDueDate(order, companyId, showLiveStart));

export const isCancelledInScope = (
  order: ExpandedOrder,
  companyId?: string
): boolean =>
  companyId
    ? typeof order.cancelReason?.[companyId] === "string"
    : !!Object.values(order.cancelReason ?? {}).length;

// Paid / Not Paid / Pre-Authorised / Overdue bucket(s) an order belongs to,
// per the FS-2861 status mapping table extended by FS-3048. Cancelled and
// Cancelled & Refunded orders are excluded from every bucket. Refund states
// win over Pre-Authorised. Overdue is always returned alongside notPaid (a
// subset), so selecting either or both chips works with a plain `.some()`
// membership check — see Table/helpers.tsx#filterPaymentStatus.
export const getPaymentStatusBuckets = (
  order: ExpandedOrder,
  refunds: IRefund[],
  companyId?: string,
  showLiveStart?: string
): PaymentStatusBucket[] => {
  if (isCancelledInScope(order, companyId)) return [];
  if (order.paymentDetails.status === OrderStatus.cancelled) return [];

  const refundStatus = getOrderRefundStatus({
    order,
    refunds,
    supplierCompanyId: companyId,
  });
  if (refundStatus) {
    if (refundStatus.label === orderStatusesMap.cancelledAndRefunded.label) {
      return [];
    }
    if (refundStatus.label === orderStatusesMap.partiallyRefunded.label) {
      return ["paid"];
    }
    // Other refund states (refund requested/processing, in-review) aren't
    // part of the ticket's mapping table — fall through to the raw status.
  }

  if (isPreAuthorisedOrder(order)) return ["preAuthorised"];
  if (order.paymentDetails.status === OrderStatus.paid) return ["paid"];

  if (isBankTransferAwaitingPayment(order)) {
    const buckets: PaymentStatusBucket[] = ["notPaid"];
    if (isOrderOverdue(order, companyId, showLiveStart)) {
      buckets.push("overdue");
    }
    return buckets;
  }

  // Any other order still awaiting payment — e.g. a Quick Order whose exhibitor
  // has not picked a payment method yet (FS-3171). Only bank transfers carry a
  // due date, so these are never Overdue.
  if (order.paymentDetails.status === OrderStatus.awaitingPayment) {
    return ["notPaid"];
  }

  return [];
};
